import React from 'react';
import PropTypes from 'prop-types';
import Separator from '@ublend-npm/aulaui-next-web/Separator';

import NoResults from '../Settings/NoResults';
import SubmissionsCard from './SubmissionsCard';
import { SubmissionGroupTitle, scrollbarStyle } from './Submissions.styled';
import formatDate from '../../../../../core/utils/formatDate';
import Scrollbar from '../../common/ScrollBar';
import SubmissionsGroupCard from './GroupSubmissions/SubmissionsGroupCard';
import Accordion from '../../common/Accordion/Accordion';

// Keys here will be used as test IDs
// for e2e test. Avoid renaming them.
// But if you must, remember to update e2e tests.
const SubmissionCategories = {
  lateSubmissions: 'Late submissions',
  earlySubmissions: 'On time submissions',
  noSubmissions: 'No submission',
};

const getGrade = (submission) => {
  if (submission.displayGrade) {
    return submission.displayGrade;
  }

  if (submission.receivedAt) {
    return 'Not graded';
  }

  return '-';
};

const SubmissionsList = ({
  loading,
  provider,
  submissions,
  assignmentId,
  ProviderConfig,
  launchAssignment,
  submissionGroups,
  isGroupAssignment,
  studentsWithNoGroup,
  accessingAsStaff,
}) => {
  if (submissions && submissions.length === 0) {
    return <NoResults primaryText="There are no submissions yet." />;
  }

  const renderSubmissions = (data) =>
    data.map((submission) =>
      isGroupAssignment ? (
        <Accordion
          key={submission.name}
          dataSet={submission.submissions}
          renderAccordionItem={(itemData) => (
            <SubmissionsCard
              key={itemData.user.id}
              user={itemData.user}
              submissionDate=""
              grade={getGrade(itemData)}
              provider={provider}
              providerConfig={ProviderConfig}
              submissionId={itemData.externalId}
              isGroup
              accessingAsStaff={accessingAsStaff}
            />
          )}
          AccordionTitleComponent={() => (
            <SubmissionsGroupCard
              name={submission.name}
              userId={submission.submissions[0].user.id || ''}
              submissionId={submission.submissions[0].externalId || ''}
              submissionDate={
                submission.receivedAt
                  ? formatDate(submission.receivedAt)
                  : 'No submission'
              }
              grade={getGrade(submission)}
              provider={provider}
              providerConfig={ProviderConfig}
              launchAssignment={launchAssignment}
              accessingAsStaff={accessingAsStaff}
            />
          )}
        />
      ) : (
        <SubmissionsCard
          key={submission.user.id}
          user={submission.user}
          grade={getGrade(submission)}
          provider={provider}
          assignmentId={assignmentId}
          providerConfig={ProviderConfig}
          submissionId={submission.externalId}
          submissionDate={
            submission.receivedAt
              ? formatDate(submission.receivedAt)
              : 'No submission'
          }
          accessingAsStaff={accessingAsStaff}
        />
      )
    );

  const renderSubmissionList = (submissionData, submissionGroup) => (
    <div key={submissionGroup}>
      <Separator />
      <SubmissionGroupTitle>
        {SubmissionCategories[submissionGroup]}{' '}
        <span data-testid={`${submissionGroup}-count`}>
          ({submissionData[submissionGroup].length})
        </span>
      </SubmissionGroupTitle>
      <div data-testid={`${submissionGroup}-container`}>
        {renderSubmissions(submissionData[submissionGroup])}
      </div>
    </div>
  );

  const loadingMessage = <div>Loading...</div>;
  const renderContent = () => {
    return Object.keys(submissionGroups).map((submissionGroup) =>
      renderSubmissionList(submissionGroups, submissionGroup)
    );
  };
  const renderStudentsWithNoGroup = () => (
    <Accordion
      dataSet={studentsWithNoGroup}
      renderAccordionItem={(student) => (
        <SubmissionsCard
          key={student.id}
          user={{
            id: student.id,
            avatar: student.avatar,
            firstName: student.firstName,
            lastName: student.lastName,
          }}
          submissionDate=""
          grade=""
          provider={provider}
          providerConfig={ProviderConfig}
          submissionId=""
          isGroup
          accessingAsStaff={accessingAsStaff}
        />
      )}
      AccordionTitleComponent={() => <p>(Not assigned to a group)</p>}
    />
  );

  return loading ? (
    loadingMessage
  ) : (
    <Scrollbar style={scrollbarStyle}>
      {renderContent()}
      {isGroupAssignment &&
        studentsWithNoGroup.length !== 0 &&
        renderStudentsWithNoGroup()}
    </Scrollbar>
  );
};

SubmissionsList.propTypes = {
  assignmentId: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  provider: PropTypes.string.isRequired,
  launchAssignment: PropTypes.func.isRequired,
  isGroupAssignment: PropTypes.bool.isRequired,
  studentsWithNoGroup: PropTypes.arrayOf(PropTypes.shape({})),
  submissions: PropTypes.arrayOf(
    PropTypes.shape({
      user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        avatar: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
      }),
      receivedAt: PropTypes.string,
      displayGrade: PropTypes.string,
    })
  ).isRequired,
  ProviderConfig: PropTypes.shape({}).isRequired,
  submissionGroups: PropTypes.shape({}).isRequired,
  accessingAsStaff: PropTypes.bool.isRequired,
};

SubmissionsList.defaultProps = {
  studentsWithNoGroup: [],
};

export default SubmissionsList;
